/**
 * Canonical Data Page
 * 
 * Displays baseline costs, suppliers, and service offerings extracted from Excel files
 * Following the pattern of StaffManagementPage
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Button, Input, Badge, LoadingSpinner, EmptyState, Select } from '@tracekit/core';
import { useWorkspace, listEntities } from '@tracekit/core';
import {
  Search,
  DollarSign,
  Building2,
  Package,
  ChevronDown,
  ChevronRight,
  Download,
  Filter,
  RefreshCw,
  TrendingUp,
} from 'lucide-react';

type CanonicalDataType = 'baseline_cost' | 'supplier' | 'service_offering';
type TabType = 'baseline_costs' | 'suppliers' | 'services';

interface BaselineCost {
  id: string;
  entity_type: string;
  data: {
    category?: string;
    description?: string;
    amount?: number;
    currency?: string;
    unit?: string;
    service_line?: string;
    project_type?: string;
    sheet_source?: string;
    cost_type?: string;
  };
  created_at?: string;
}

interface Supplier {
  id: string;
  entity_type: string;
  data: {
    name?: string;
    contact_info?: any;
    contact_name?: string;
    contact_email?: string;
    contact_phone?: string;
    address?: string;
    services_offered?: string[];
    specializations?: string[];
    service_line?: string;
    certifications?: string[];
    performance_rating?: number;
  };
  created_at?: string;
}

interface ServiceOffering {
  id: string;
  entity_type: string;
  data: {
    name?: string;
    category?: string;
    description?: string;
    service_line?: string;
    duration?: string;
    cost_estimate?: number;
    unit_cost?: number;
    currency?: string;
    deliverables?: string[];
  };
  created_at?: string;
}

export function CanonicalDataPage() {
  const { projectId } = useWorkspace();
  const [activeTab, setActiveTab] = useState<TabType>('baseline_costs');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedServiceLine, setSelectedServiceLine] = useState<string>('all');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Fetch baseline costs
  const { data: baselineCostsData, isLoading: costsLoading, refetch: refetchCosts } = useQuery({
    queryKey: ['baseline-costs', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const entities = await listEntities(projectId, 'baseline_cost', 1000);
      return entities.map(e => ({
        id: e.id,
        entity_type: e.entity_type || 'baseline_cost',
        data: (e.data || {}) as BaselineCost['data'],
        created_at: e.created_at || new Date().toISOString(),
      })) as BaselineCost[];
    },
    enabled: !!projectId,
  });

  // Fetch suppliers
  const { data: suppliersData, isLoading: suppliersLoading, refetch: refetchSuppliers } = useQuery({
    queryKey: ['suppliers', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const entities = await listEntities(projectId, 'supplier', 1000);
      return entities.map(e => ({
        id: e.id,
        entity_type: e.entity_type || 'supplier',
        data: (e.data || {}) as Supplier['data'],
        created_at: e.created_at || new Date().toISOString(),
      })) as Supplier[];
    },
    enabled: !!projectId,
  });

  // Fetch service offerings
  const { data: servicesData, isLoading: servicesLoading, refetch: refetchServices } = useQuery({
    queryKey: ['service-offerings', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const entities = await listEntities(projectId, 'service_offering', 1000);
      return entities.map(e => ({
        id: e.id,
        entity_type: e.entity_type || 'service_offering',
        data: (e.data || {}) as ServiceOffering['data'],
        created_at: e.created_at || new Date().toISOString(),
      })) as ServiceOffering[];
    },
    enabled: !!projectId,
  });

  // Filter and search baseline costs
  const filteredBaselineCosts = useMemo(() => {
    if (!baselineCostsData) return [];
    let filtered = baselineCostsData;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        cost =>
          cost.data.description?.toLowerCase().includes(searchLower) ||
          cost.data.category?.toLowerCase().includes(searchLower) ||
          cost.data.service_line?.toLowerCase().includes(searchLower)
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(cost => cost.data.category === selectedCategory);
    }

    if (selectedServiceLine !== 'all') {
      filtered = filtered.filter(cost => cost.data.service_line === selectedServiceLine);
    }

    return filtered;
  }, [baselineCostsData, searchTerm, selectedCategory, selectedServiceLine]);

  // Filter and search suppliers
  const filteredSuppliers = useMemo(() => {
    if (!suppliersData) return [];
    let filtered = suppliersData;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        supplier =>
          supplier.data.name?.toLowerCase().includes(searchLower) ||
          supplier.data.specializations?.some(s => s.toLowerCase().includes(searchLower)) ||
          supplier.data.services_offered?.some(s => s.toLowerCase().includes(searchLower))
      );
    }

    if (selectedServiceLine !== 'all') {
      filtered = filtered.filter(supplier => supplier.data.service_line === selectedServiceLine);
    }

    return filtered;
  }, [suppliersData, searchTerm, selectedServiceLine]);

  // Filter and search services
  const filteredServices = useMemo(() => {
    if (!servicesData) return [];
    let filtered = servicesData;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        service =>
          service.data.name?.toLowerCase().includes(searchLower) ||
          service.data.description?.toLowerCase().includes(searchLower) ||
          service.data.category?.toLowerCase().includes(searchLower)
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(service => service.data.category === selectedCategory);
    }

    if (selectedServiceLine !== 'all') {
      filtered = filtered.filter(service => service.data.service_line === selectedServiceLine);
    }

    return filtered;
  }, [servicesData, searchTerm, selectedCategory, selectedServiceLine]);

  // Get unique categories and service lines for filters
  const costCategories = useMemo(() => {
    if (!baselineCostsData) return [];
    const categories = new Set(baselineCostsData.map(c => c.data.category).filter(Boolean));
    return Array.from(categories).sort();
  }, [baselineCostsData]);

  const serviceCategories = useMemo(() => {
    if (!servicesData) return [];
    const categories = new Set(servicesData.map(s => s.data.category).filter(Boolean));
    return Array.from(categories).sort();
  }, [servicesData]);

  const serviceLines = useMemo(() => {
    const lines = new Set<string>();
    baselineCostsData?.forEach(c => c.data.service_line && lines.add(c.data.service_line));
    suppliersData?.forEach(s => s.data.service_line && lines.add(s.data.service_line));
    servicesData?.forEach(s => s.data.service_line && lines.add(s.data.service_line));
    return Array.from(lines).sort();
  }, [baselineCostsData, suppliersData, servicesData]);

  // Calculate stats
  const stats = useMemo(() => {
    const totalCosts = baselineCostsData?.length || 0;
    const totalAmount = baselineCostsData?.reduce((sum, c) => sum + (c.data.amount || 0), 0) || 0;
    const totalSuppliers = suppliersData?.length || 0;
    const totalServices = servicesData?.length || 0;

    return {
      totalCosts,
      totalAmount,
      totalSuppliers,
      totalServices,
    };
  }, [baselineCostsData, suppliersData, servicesData]);

  const toggleRowExpansion = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleExport = (type: TabType) => {
    let data: any[] = [];
    let filename = '';

    switch (type) {
      case 'baseline_costs':
        data = filteredBaselineCosts;
        filename = 'baseline_costs.csv';
        break;
      case 'suppliers':
        data = filteredSuppliers;
        filename = 'suppliers.csv';
        break;
      case 'services':
        data = filteredServices;
        filename = 'service_offerings.csv';
        break;
    }

    // Simple CSV export
    if (data.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = Object.keys(data[0].data || {});
    const csvRows = [
      headers.join(','),
      ...data.map(row => headers.map(h => JSON.stringify(row.data[h] || '')).join(','))
    ];
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isLoading = costsLoading || suppliersLoading || servicesLoading;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Baseline Costs</p>
              <p className="text-2xl font-bold">{stats.totalCosts}</p>
              <p className="text-xs text-gray-500">
                ${stats.totalAmount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-green-600" />
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Suppliers</p>
              <p className="text-2xl font-bold">{stats.totalSuppliers}</p>
            </div>
            <Building2 className="h-8 w-8 text-blue-600" />
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Service Offerings</p>
              <p className="text-2xl font-bold">{stats.totalServices}</p>
            </div>
            <Package className="h-8 w-8 text-purple-600" />
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Records</p>
              <p className="text-2xl font-bold">{stats.totalCosts + stats.totalSuppliers + stats.totalServices}</p>
            </div>
            <TrendingUp className="h-8 w-8 text-orange-600" />
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Card>
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex gap-4">
            {[
              { id: 'baseline_costs', label: 'Baseline Costs', icon: DollarSign },
              { id: 'suppliers', label: 'Suppliers', icon: Building2 },
              { id: 'services', label: 'Service Offerings', icon: Package },
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as TabType)}
                  className={`px-4 py-2 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          {activeTab === 'baseline_costs' && (
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-48"
            >
              <option value="all">All Categories</option>
              {costCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </Select>
          )}
          {activeTab === 'services' && (
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-48"
            >
              <option value="all">All Categories</option>
              {serviceCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </Select>
          )}
          <Select
            value={selectedServiceLine}
            onChange={(e) => setSelectedServiceLine(e.target.value)}
            className="w-48"
          >
            <option value="all">All Service Lines</option>
            {serviceLines.map(line => (
              <option key={line} value={line}>{line}</option>
            ))}
          </Select>
          <Button
            variant="outline"
            onClick={() => {
              refetchCosts();
              refetchSuppliers();
              refetchServices();
            }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport(activeTab)}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>

        {/* Content */}
        <div className="p-4">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {/* Baseline Costs Tab */}
              {activeTab === 'baseline_costs' && (
                <>
                  {filteredBaselineCosts.length === 0 ? (
                    <EmptyState
                      icon={<DollarSign className="h-12 w-12" />}
                      title="No Baseline Costs Found"
                      description="No baseline costs match your search criteria."
                    />
                  ) : (
                    <div className="space-y-2">
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Showing {filteredBaselineCosts.length} of {baselineCostsData?.length || 0} baseline costs
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-200 dark:border-gray-700">
                              <th className="text-left p-2">Category</th>
                              <th className="text-left p-2">Description</th>
                              <th className="text-left p-2">Amount</th>
                              <th className="text-left p-2">Unit</th>
                              <th className="text-left p-2">Service Line</th>
                              <th className="text-left p-2">Project Type</th>
                              <th className="text-left p-2 w-12"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredBaselineCosts.map((cost) => (
                              <React.Fragment key={cost.id}>
                                <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900">
                                  <td className="p-2">
                                    <Badge variant="secondary">{cost.data.category || 'N/A'}</Badge>
                                  </td>
                                  <td className="p-2">{cost.data.description || 'N/A'}</td>
                                  <td className="p-2">
                                    {cost.data.amount
                                      ? `${cost.data.currency || 'CAD'} ${cost.data.amount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                      : 'N/A'}
                                  </td>
                                  <td className="p-2">{cost.data.unit || 'N/A'}</td>
                                  <td className="p-2">{cost.data.service_line || 'N/A'}</td>
                                  <td className="p-2">{cost.data.project_type || 'N/A'}</td>
                                  <td className="p-2">
                                    <button
                                      onClick={() => toggleRowExpansion(cost.id)}
                                      className="text-gray-400 hover:text-gray-600"
                                    >
                                      {expandedRows.has(cost.id) ? (
                                        <ChevronDown className="h-4 w-4" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4" />
                                      )}
                                    </button>
                                  </td>
                                </tr>
                                {expandedRows.has(cost.id) && (
                                  <tr>
                                    <td colSpan={7} className="p-4 bg-gray-50 dark:bg-gray-900">
                                      <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                          <strong>Sheet Source:</strong> {cost.data.sheet_source || 'N/A'}
                                        </div>
                                        <div>
                                          <strong>Cost Type:</strong> {cost.data.cost_type || 'N/A'}
                                        </div>
                                        <div className="col-span-2">
                                          <strong>Full Data:</strong>
                                          <pre className="mt-2 p-2 bg-white dark:bg-gray-800 rounded text-xs overflow-auto">
                                            {JSON.stringify(cost.data, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Suppliers Tab */}
              {activeTab === 'suppliers' && (
                <>
                  {filteredSuppliers.length === 0 ? (
                    <EmptyState
                      icon={<Building2 className="h-12 w-12" />}
                      title="No Suppliers Found"
                      description="No suppliers match your search criteria."
                    />
                  ) : (
                    <div className="space-y-2">
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Showing {filteredSuppliers.length} of {suppliersData?.length || 0} suppliers
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-200 dark:border-gray-700">
                              <th className="text-left p-2">Name</th>
                              <th className="text-left p-2">Contact</th>
                              <th className="text-left p-2">Services Offered</th>
                              <th className="text-left p-2">Specializations</th>
                              <th className="text-left p-2">Service Line</th>
                              <th className="text-left p-2">Rating</th>
                              <th className="text-left p-2 w-12"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredSuppliers.map((supplier) => (
                              <React.Fragment key={supplier.id}>
                                <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900">
                                  <td className="p-2 font-medium">{supplier.data.name || 'N/A'}</td>
                                  <td className="p-2">
                                    <div className="text-sm">
                                      {supplier.data.contact_name && <div>{supplier.data.contact_name}</div>}
                                      {supplier.data.contact_email && <div className="text-gray-500">{supplier.data.contact_email}</div>}
                                      {supplier.data.contact_phone && <div className="text-gray-500">{supplier.data.contact_phone}</div>}
                                    </div>
                                  </td>
                                  <td className="p-2">
                                    {supplier.data.services_offered && supplier.data.services_offered.length > 0 ? (
                                      <div className="flex flex-wrap gap-1">
                                        {supplier.data.services_offered.slice(0, 2).map((service, idx) => (
                                          <Badge key={idx} variant="secondary" className="text-xs">
                                            {service}
                                          </Badge>
                                        ))}
                                        {supplier.data.services_offered.length > 2 && (
                                          <Badge variant="secondary" className="text-xs">
                                            +{supplier.data.services_offered.length - 2}
                                          </Badge>
                                        )}
                                      </div>
                                    ) : (
                                      'N/A'
                                    )}
                                  </td>
                                  <td className="p-2">
                                    {supplier.data.specializations && supplier.data.specializations.length > 0 ? (
                                      <div className="flex flex-wrap gap-1">
                                        {supplier.data.specializations.slice(0, 2).map((spec, idx) => (
                                          <Badge key={idx} variant="outline" className="text-xs">
                                            {spec}
                                          </Badge>
                                        ))}
                                        {supplier.data.specializations.length > 2 && (
                                          <Badge variant="outline" className="text-xs">
                                            +{supplier.data.specializations.length - 2}
                                          </Badge>
                                        )}
                                      </div>
                                    ) : (
                                      'N/A'
                                    )}
                                  </td>
                                  <td className="p-2">{supplier.data.service_line || 'N/A'}</td>
                                  <td className="p-2">
                                    {supplier.data.performance_rating ? (
                                      <Badge variant={supplier.data.performance_rating >= 4 ? 'success' : supplier.data.performance_rating >= 3 ? 'warning' : 'error'}>
                                        {supplier.data.performance_rating}/5
                                      </Badge>
                                    ) : (
                                      'N/A'
                                    )}
                                  </td>
                                  <td className="p-2">
                                    <button
                                      onClick={() => toggleRowExpansion(supplier.id)}
                                      className="text-gray-400 hover:text-gray-600"
                                    >
                                      {expandedRows.has(supplier.id) ? (
                                        <ChevronDown className="h-4 w-4" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4" />
                                      )}
                                    </button>
                                  </td>
                                </tr>
                                {expandedRows.has(supplier.id) && (
                                  <tr>
                                    <td colSpan={7} className="p-4 bg-gray-50 dark:bg-gray-900">
                                      <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                          <strong>Address:</strong> {supplier.data.address || 'N/A'}
                                        </div>
                                        <div>
                                          <strong>Certifications:</strong>{' '}
                                          {supplier.data.certifications && supplier.data.certifications.length > 0
                                            ? supplier.data.certifications.join(', ')
                                            : 'N/A'}
                                        </div>
                                        <div className="col-span-2">
                                          <strong>Full Data:</strong>
                                          <pre className="mt-2 p-2 bg-white dark:bg-gray-800 rounded text-xs overflow-auto">
                                            {JSON.stringify(supplier.data, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Services Tab */}
              {activeTab === 'services' && (
                <>
                  {filteredServices.length === 0 ? (
                    <EmptyState
                      icon={<Package className="h-12 w-12" />}
                      title="No Service Offerings Found"
                      description="No service offerings match your search criteria."
                    />
                  ) : (
                    <div className="space-y-2">
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Showing {filteredServices.length} of {servicesData?.length || 0} service offerings
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-200 dark:border-gray-700">
                              <th className="text-left p-2">Name</th>
                              <th className="text-left p-2">Category</th>
                              <th className="text-left p-2">Description</th>
                              <th className="text-left p-2">Service Line</th>
                              <th className="text-left p-2">Duration</th>
                              <th className="text-left p-2">Cost Estimate</th>
                              <th className="text-left p-2 w-12"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredServices.map((service) => (
                              <React.Fragment key={service.id}>
                                <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900">
                                  <td className="p-2 font-medium">{service.data.name || 'N/A'}</td>
                                  <td className="p-2">
                                    <Badge variant="secondary">{service.data.category || 'N/A'}</Badge>
                                  </td>
                                  <td className="p-2">{service.data.description || 'N/A'}</td>
                                  <td className="p-2">{service.data.service_line || 'N/A'}</td>
                                  <td className="p-2">{service.data.duration || 'N/A'}</td>
                                  <td className="p-2">
                                    {service.data.cost_estimate
                                      ? `${service.data.currency || 'CAD'} ${service.data.cost_estimate.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                      : 'N/A'}
                                  </td>
                                  <td className="p-2">
                                    <button
                                      onClick={() => toggleRowExpansion(service.id)}
                                      className="text-gray-400 hover:text-gray-600"
                                    >
                                      {expandedRows.has(service.id) ? (
                                        <ChevronDown className="h-4 w-4" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4" />
                                      )}
                                    </button>
                                  </td>
                                </tr>
                                {expandedRows.has(service.id) && (
                                  <tr>
                                    <td colSpan={7} className="p-4 bg-gray-50 dark:bg-gray-900">
                                      <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                          <strong>Unit Cost:</strong>{' '}
                                          {service.data.unit_cost
                                            ? `${service.data.currency || 'CAD'} ${service.data.unit_cost.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                            : 'N/A'}
                                        </div>
                                        <div>
                                          <strong>Deliverables:</strong>{' '}
                                          {service.data.deliverables && service.data.deliverables.length > 0
                                            ? service.data.deliverables.join(', ')
                                            : 'N/A'}
                                        </div>
                                        <div className="col-span-2">
                                          <strong>Full Data:</strong>
                                          <pre className="mt-2 p-2 bg-white dark:bg-gray-800 rounded text-xs overflow-auto">
                                            {JSON.stringify(service.data, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
